/**
 * Modal de configuração para node Encerrar Fluxo (end_flow)
 *
 * Permite configurar:
 *  - Mensagem opcional de despedida (com suporte a variáveis)
 *  - Fechar conversa ao final (status sempre "Encerrada" via RPC close_conversation)
 *
 * RELEASE: 1.0.1 - 2026-05-18 - Remove seletor de status (RPC close_conversation sempre aplica 'closed'). Substitui por info banner.
 * RELEASE: 1.0.0 - 2026-05-18 - Implementação inicial (gatilho inatividade)
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Info } from 'lucide-react';
import { FlowNode, EndFlowConfig } from '@/types/flows';
import { toast } from 'sonner';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

interface ConfigureEndFlowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: EndFlowConfig) => void;
}

/**
 * Dialog de configuração do node Encerrar Fluxo
 */
export function ConfigureEndFlowDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureEndFlowDialogProps) {
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  // Estado local da configuração
  const [farewellMessage, setFarewellMessage] = useState('');
  const [closeConversation, setCloseConversation] = useState(false);

  // Sincroniza estados quando node mudar / dialog abrir
  useEffect(() => {
    if (node && open) {
      const cfg = (node.data.config || {}) as EndFlowConfig;
      setFarewellMessage(cfg.farewell_message || '');
      setCloseConversation(!!cfg.close_conversation);
    }
  }, [node, open]);

  /**
   * Salva configuração após sanitizar limites de tamanho
   */
  const handleSave = () => {
    if (!node) return;

    // Sanitização básica (limite anti-injection / payload abuse)
    const trimmed = (farewellMessage || '').trim().slice(0, 4096);

    const config: EndFlowConfig = {
      nickname: nicknameRef.current?.getNickname(),
      farewell_message: trimmed || undefined,
      close_conversation: closeConversation,
      // Status sempre 'closed' — RPC close_conversation só aplica este status
      conversation_status: closeConversation ? 'closed' : undefined,
    };

    onSave(node.id, config);
    toast.success('Configuração salva com sucesso');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Configurar Encerramento de Fluxo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Campo ID do Node (somente leitura + nickname editável) */}
          <NodeIdField
            ref={nicknameRef}
            nodeId={node?.id}
            defaultNickname={(node?.data.config as EndFlowConfig | undefined)?.nickname}
          />

          {/* Mensagem de despedida (opcional) */}
          <div className="space-y-2">
            <Label htmlFor="farewell-message">Mensagem de despedida (opcional)</Label>
            <Textarea
              id="farewell-message"
              placeholder="Ex.: Sua conversa foi encerrada por inatividade. Volte sempre!"
              value={farewellMessage}
              onChange={(e) => setFarewellMessage(e.target.value)}
              maxLength={4096}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              Enviada via canal da conversa antes de encerrar. Suporta variáveis
              do fluxo (ex.: <code>{`{{contact_name}}`}</code>).
            </p>
          </div>

          {/* Fechar conversa */}
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="close-conv">Encerrar conversa ao final</Label>
              <p className="text-xs text-muted-foreground">
                Altera o status da conversa após executar este node
              </p>
            </div>
            <Switch
              id="close-conv"
              checked={closeConversation}
              onCheckedChange={setCloseConversation}
            />
          </div>

          {/* Aviso informativo: status sempre "Encerrada" via RPC close_conversation */}
          {closeConversation && (
            <div className="flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 p-3">
              <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Status aplicado: Encerrada</p>
                <p className="text-xs text-muted-foreground">
                  A conversa será encerrada com status <strong>Encerrada</strong>.
                </p>
              </div>
            </div>
          )}

          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-sm font-medium mb-1">Resumo:</p>
            <p className="text-sm text-muted-foreground">
              {farewellMessage.trim()
                ? 'Enviará a mensagem de despedida e em seguida'
                : 'Encerrará o fluxo'}
              {closeConversation
                ? ' fechará a conversa com status "Encerrada".'
                : ' sem alterar o status da conversa.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Salvar Configuração</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
